'use client'

import { createContext, useEffect, useMemo, useRef, useState } from 'react'
import { signIn, signOut, useSession } from 'next-auth/react'
import { useRouter } from 'next/navigation'
import type { AuthContextValue, LoginCredentials, LoginResult } from '@/lib/commerce/types/auth'

export const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ vendor, initialCustomer, children }: { vendor: any, initialCustomer: any, children: React.ReactNode }) {
  const { data: session, status, update } = useSession()
  const router = useRouter()
  const [customer] = useState<any>(initialCustomer)
  const [guestSessionCreated, setGuestSessionCreated] = useState(false)
  const [currentVendorId, setCurrentVendorId] = useState<string | null>(null)
  const hasReloadedRef = useRef(false)

  const isLoggedIn = useMemo(() => !!session?.user?.id, [session])
  const isGuest = useMemo(() => !session?.user?.id, [session])

  const sessionId = useMemo(() => {
    if (!session) return null
    return isGuest ? (session as any).sessionId : (session.user?.id || (session as any).sessionId)
  }, [session, isGuest])

  useEffect(() => {
    if (!vendor?.id) return
    if (currentVendorId && vendor.id !== currentVendorId && !hasReloadedRef.current) {
      hasReloadedRef.current = true
      window.location.reload()
      return
    }
    if (currentVendorId === vendor.id) return
    setCurrentVendorId(vendor.id)
    if (session && session?.user?.vendorId !== vendor.id) {
      update({ user: { ...session.user, vendorId: vendor.id } }).then(() => router.refresh()).catch(err => console.error('Error updating session vendor:', err))
    }
  }, [vendor?.id, session])

  useEffect(() => {
    if (status !== 'unauthenticated' || !vendor || guestSessionCreated) return
    const createGuestSession = async () => {
      try {
        const result = await signIn('credentials', { isGuest: 'true', vendorId: vendor?.id || vendor, redirect: false })
        if (result?.ok) setGuestSessionCreated(true)
      } catch (error) {
        console.error('Error creating guest session:', error)
      }
    }
    createGuestSession()
  }, [status, vendor, guestSessionCreated])

  const login = async ({ emailOrPhone, password, sessionId: passedSessionId }: LoginCredentials): Promise<LoginResult> => {
    const response = await signIn('credentials', {
      email: emailOrPhone,
      password,
      sessionId: passedSessionId ?? (session as any)?.sessionId,
      vendorId: vendor?.id,
      redirect: false,
    })
    if (!response) return { ok: false, error: 'Нэвтрэхэд алдаа гарлаа' }
    if (response.error) return { ok: false, error: response.error }
    if (response.ok === false) return { ok: false, error: 'Нэвтрэхэд алдаа гарлаа' }
    return { ok: true }
  }

  const logout = async () => {
    await signOut({ redirect: false })
  }

  return (
    <AuthContext.Provider value={{ session, status, customer, vendor, sessionId, isLoggedIn, isGuest, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export default AuthProvider
